import {BaseCommand} from './base.command';
import {TextChangeSnapshot, UndoableCommand} from '../interfaces/command.interface';
import {CytoscapeService} from '../../cy-services/cytoscape.service';
import {NodeService} from '../../../api/services/node.service';
import {firstValueFrom} from 'rxjs';
import {UndoLabelMetadata} from '../undo-label.utils';

/**
 * TextChangeCommand handles text field changes with support for coalescing.
 * Multiple changes within the same focus session can be merged into a single command.
 */
export class TextChangeCommand extends BaseCommand<TextChangeSnapshot> {
  private focusSessionId: string;
  private lastUpdateTime: number;

  constructor(
    private cyService: CytoscapeService,
    private nodeService: NodeService,
    snapshot: TextChangeSnapshot,
    focusSessionId: string
  ) {
    // Map field to display name
    const partName = snapshot.field === 'name' ? 'Name' : snapshot.field === 'description' ? 'Description' : 'Text';
    const objectType = snapshot.entityType === 'node' ? 'Node' : 'Link';

    const labelMetadata: UndoLabelMetadata = {
      object: objectType,
      verb: 'Change',
      part: partName
    };

    super(
      'text-change',
      `${objectType} ${partName} Change`,
      {before: snapshot, after: {...snapshot}},
      labelMetadata
    );
    this.focusSessionId = focusSessionId;
    this.lastUpdateTime = Date.now();
  }

  async execute(): Promise<void> {
    const cy = this.cyService.cy;
    if (!cy) return;

    const {entityId, entityType, field, after} = this.snapshot.after;

    // Update Cytoscape data
    const element = cy.$(`#${entityId}`);
    if (element.length > 0) {
      element.data(field, after);
    }

    // Update on backend
    const payload = {id: entityId, [field]: after};

    if (entityType === 'node') {
      await firstValueFrom(this.nodeService.updateNode(payload as any));
    } else {
      await firstValueFrom(this.nodeService.updateLink(payload as any));
    }

    cy.style().update();
  }

  async undo(): Promise<void> {
    const cy = this.cyService.cy;
    if (!cy) return;

    const {entityId, entityType, field, before} = this.snapshot.before;

    // Restore previous value in Cytoscape
    const element = cy.$(`#${entityId}`);
    if (element.length > 0) {
      element.data(field, before);
    }

    // Update on backend
    const payload = {id: entityId, [field]: before};

    if (entityType === 'node') {
      await firstValueFrom(this.nodeService.updateNode(payload as any));
    } else {
      await firstValueFrom(this.nodeService.updateLink(payload as any));
    }

    cy.style().update();
  }

  /**
   * Check if this command can be merged with another.
   * Commands can be merged if they're for the same entity, field, and focus session.
   */
  override canMerge(other: UndoableCommand): boolean {
    if (!(other instanceof TextChangeCommand)) return false;
    if (other.focusSessionId !== this.focusSessionId) return false;
    if (other.snapshot.before.entityId !== this.snapshot.before.entityId) return false;
    if (other.snapshot.before.field !== this.snapshot.before.field) return false;
    return true;
  }

  /**
   * Merge another text change command into this one.
   * Keeps the original "before" value but updates the "after" value.
   */
  override merge(other: UndoableCommand): void {
    if (!(other instanceof TextChangeCommand)) return;

    // Keep original 'before', update 'after' to the latest value
    this.snapshot.after.after = other.snapshot.after.after;
    this.lastUpdateTime = other.lastUpdateTime;
  }
}
